import React, { useMemo } from 'react';
import styled from 'styled-components';

import { ApiWallet } from 'api/types';
import MaterialSymbol from 'components/basic/icons/MaterialSymbol';
import DataPair from 'components/basic/panels/DataPair';
import TextColored from 'components/basic/text/TextColored';
import Tooltip from 'components/basic/tooltip/Tooltip';
import {
  availableCollateralDescription,
  balanceDescription,
  freeCollateralDescription,
} from 'components/wallet/walletSharedTooltips';
import { formatUsdWithSign } from 'lib/display/formatting';
import { useSelector } from 'reduxState/storeCore';

import BalanceStatsPendingFunds from './BalanceStatsPendingFunds';

const InfoIconWrap = styled.div<{ onClick?: () => void }>`
  display: inline-block;
  position: relative;
  bottom: -2px;
  font-size: 11px;
  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'initial')};
`;

// TODO: reuse Info Bubble across app
function DesktopOnlyInfoBubble({ text }: { text: string }) {
  const isMobile = useSelector(state => state.screen.isMobile);
  return isMobile ? null : (
    <Tooltip text={text} maxWidthPx={230}>
      <InfoIconWrap>
        <MaterialSymbol icon="info" fontSize="inherit" />
      </InfoIconWrap>
    </Tooltip>
  );
}

/**
 * Panel with core stats like Balance, Free Collateral, Available Collateral, Unrealized P&L.
 * Used in BuySell panel and Withdrawal screen.
 */
export default function BalanceStatsPanel({
  onlyCollateral,
  walletWithPositions,
}: {
  onlyCollateral?: boolean;
  walletWithPositions:
    | Pick<
        ApiWallet,
        'equity' | 'availableCollateral' | 'freeCollateral' | 'unrealizedPnL'
      >
    | undefined;
}) {
  const { balance, availableCollateral, freeCollateral, unrealizedPnL } =
    useMemo(() => {
      if (!walletWithPositions) {
        return {
          balance: '-',
          availableCollateral: '-',
          freeCollateral: '-',
          unrealizedPnL: '-',
        };
      }
      return {
        balance: formatUsdWithSign(walletWithPositions.equity),
        availableCollateral: formatUsdWithSign(
          walletWithPositions?.availableCollateral
        ),
        freeCollateral: formatUsdWithSign(walletWithPositions?.freeCollateral),
        unrealizedPnL: formatUsdWithSign(walletWithPositions?.unrealizedPnL),
      };
    }, [walletWithPositions]);

  if (onlyCollateral) {
    return (
      <DataPair
        label={
          <>
            Available Collateral{' '}
            <DesktopOnlyInfoBubble text={availableCollateralDescription} />
          </>
        }
        noPadding
      >
        {availableCollateral}
      </DataPair>
    );
  }

  return (
    <>
      <DataPair
        label={
          <>
            Balance <DesktopOnlyInfoBubble text={balanceDescription} />
          </>
        }
        noPadding
      >
        {balance}
      </DataPair>
      <DataPair
        label={
          <>
            Free Collateral{' '}
            <DesktopOnlyInfoBubble text={freeCollateralDescription} />
          </>
        }
        noPadding
      >
        {freeCollateral}
      </DataPair>
      <DataPair
        label={
          <>
            Available Collateral{' '}
            <DesktopOnlyInfoBubble text={availableCollateralDescription} />
          </>
        }
        noPadding
      >
        {availableCollateral}
      </DataPair>
      <DataPair label="Unrealized P&L" noPadding>
        <TextColored
          color={
            !walletWithPositions ||
            Number(walletWithPositions.unrealizedPnL) === 0
              ? 'default'
              : Number(walletWithPositions.unrealizedPnL) > 0
              ? 'green'
              : 'red'
          }
        >
          {unrealizedPnL}
        </TextColored>
      </DataPair>

      <BalanceStatsPendingFunds />
    </>
  );
}
